/**
 * JuryForm Component
 * ==================
 * Flexible jury member management - add/edit/delete members with any function.
 * Based on the original JurryFourm.js implementation.
 */

import React, { useState } from 'react';
import { searchMembers } from '../api/ussaApi';
import './JuryForm.css';

const JURY_FUNCTIONS = [
  { value: 'CHIEFCOMPETITION', label: 'Chief of Competition' },
  { value: 'TECHNICALDELEGATE', label: 'Technical Delegate' },
  { value: 'RACEDIRECTOR', label: 'Race Director' },
  { value: 'RACEDIRECTORASSISTANT', label: 'Race Director Assistant' },
  { value: 'ASSISTANT', label: 'TD Assistant' },
  { value: 'TECHNICALDELEGATEASSISTANTNATIONAL', label: 'TD Assistant (National)' },
  { value: 'MEMBER', label: 'Member' },
];

const NATIONS = [
  "AUS","AUT","BEL","BUL","CAN","CHI","CHN","CRO","CZE","DEN","ESP","EST","FIN","FRA",
  "GBR","GER","GRE","HUN","IRL","ISL","ISR","ITA","JPN","KAZ","KOR","LAT","LIE","LTU",
  "LUX","MEX","MGL","MON","NED","NOR","NZL","POL","POR","ROU","RSA","RUS","SLO","SUI",
  "SVK","SWE","TUR","UKR","USA","UZB",
];

function JuryForm({ jury, setJury, ussaEnabled }) {
  // Ensure jury is always an array
  const safeJury = Array.isArray(jury) ? jury : [];
  
  // Form state for adding new member
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMember, setNewMember] = useState({
    Function: 'CHIEFCOMPETITION',
    FirstName: '',
    LastName: '',
    Nation: 'USA',
    MemberNumber: '',
  });
  
  // Edit state
  const [editIndex, setEditIndex] = useState(null);
  const [editMember, setEditMember] = useState(null);
  
  // Search state
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchTarget, setSearchTarget] = useState(null); // 'new' or index number

  // Add new jury member
  const handleAddMember = () => {
    if (!newMember.FirstName || !newMember.LastName) {
      alert('Please enter first and last name');
      return;
    }
    setJury([...safeJury, { ...newMember }]);
    setNewMember({
      Function: 'CHIEFCOMPETITION',
      FirstName: '',
      LastName: '',
      Nation: newMember.Nation, // Keep last used nation
      MemberNumber: '',
    });
    setShowAddForm(false);
  };

  // Delete jury member
  const handleDelete = (index) => {
    setJury(safeJury.filter((_, i) => i !== index));
  };

  // Start editing
  const handleStartEdit = (index) => {
    setEditIndex(index);
    setEditMember({ ...safeJury[index] });
  };

  // Save edit
  const handleSaveEdit = () => {
    const updated = [...safeJury];
    updated[editIndex] = editMember;
    setJury(updated);
    setEditIndex(null);
    setEditMember(null);
  };

  // Cancel edit
  const handleCancelEdit = () => {
    setEditIndex(null);
    setEditMember(null);
  };

  // Search USSA members
  const handleSearch = async (target) => {
    const member = target === 'new' ? newMember : editMember;
    const firstName = member?.FirstName?.trim();
    const lastName = member?.LastName?.trim();
    
    if (!firstName && !lastName) {
      alert('Enter a first or last name to search');
      return;
    }
    
    setSearchTarget(target);
    setIsSearching(true);
    setSearchResults([]);
    
    try {
      // searchMembers expects { firstName, lastName, limit } with wildcard strings
      const results = await searchMembers({
        firstName: firstName ? `*${firstName}*` : undefined,
        lastName: lastName ? `*${lastName}*` : undefined,
        limit: 15,
      });
      
      if (!results || results.length === 0) {
        alert('No members found matching that name');
      }
      setSearchResults(results || []);
    } catch (err) {
      console.error('Search error:', err);
      alert(`Search failed: ${err.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  // Select search result
  const handleSelectResult = (member) => {
    const update = {
      FirstName: member.firstName || '',
      LastName: member.lastName || '',
      MemberNumber: member.id?.toString() || '',
    };
    
    if (searchTarget === 'new') {
      setNewMember(prev => ({ ...prev, ...update }));
    } else if (typeof searchTarget === 'number') {
      setEditMember(prev => ({ ...prev, ...update }));
    }
    
    setSearchResults([]);
    setSearchTarget(null);
  };

  // Check if member is active for current season
  const getMemberStatus = (member) => {
    if (member.inactiveStatus) {
      return { label: member.inactiveStatus, className: 'status-inactive' };
    }
    if (member.memberType === 'non-member') {
      return { label: 'Non-member', className: 'status-inactive' };
    }
    // Check if current for recent season
    const currentSeason = new Date().getFullYear();
    const isCurrentSeason = member.seasons?.some(
      s => s.season >= currentSeason - 1 && s.flags?.current
    );
    if (isCurrentSeason) {
      return { label: 'Active', className: 'status-active' };
    }
    return { label: 'Inactive', className: 'status-inactive' };
  };

  const getFunctionLabel = (value) => {
    return JURY_FUNCTIONS.find(f => f.value === value)?.label || value;
  };

  return (
    <div className="jury-form-v2">
      {/* Jury List */}
      {safeJury.length === 0 ? (
        <p className="no-jury">No jury members added yet.</p>
      ) : (
        <table className="jury-table">
          <thead>
            <tr>
              <th>Function</th>
              <th>Name</th>
              <th>Nation</th>
              {ussaEnabled && <th>USSA #</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {safeJury.map((member, index) => (
              <tr key={index}>
                {editIndex === index ? (
                  // Edit mode
                  <>
                    <td>
                      <select
                        value={editMember.Function}
                        onChange={(e) => setEditMember({ ...editMember, Function: e.target.value })}
                      >
                        {JURY_FUNCTIONS.map(f => (
                          <option key={f.value} value={f.value}>{f.label}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={editMember.FirstName}
                        onChange={(e) => setEditMember({ ...editMember, FirstName: e.target.value })}
                        placeholder="First"
                      />
                      <input
                        type="text"
                        value={editMember.LastName}
                        onChange={(e) => setEditMember({ ...editMember, LastName: e.target.value })}
                        placeholder="Last"
                      />
                    </td>
                    <td>
                      <select
                        value={editMember.Nation}
                        onChange={(e) => setEditMember({ ...editMember, Nation: e.target.value })}
                      >
                        {NATIONS.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </td>
                    {ussaEnabled && (
                      <td>
                        <button 
                          type="button" 
                          className="search-btn"
                          onClick={() => handleSearch(index)}
                          title="Search USSA"
                        >
                          🔍
                        </button>
                        <input
                          type="text"
                          value={editMember.MemberNumber || ''}
                          onChange={(e) => setEditMember({ ...editMember, MemberNumber: e.target.value })}
                          placeholder="USSA ID"
                        />
                      </td>
                    )}
                    <td className="action-buttons">
                      <button onClick={handleSaveEdit} className="save-btn">✓</button>
                      <button onClick={handleCancelEdit} className="cancel-btn">✕</button>
                    </td>
                  </>
                ) : (
                  // Display mode
                  <>
                    <td>{getFunctionLabel(member.Function)}</td>
                    <td>{member.FirstName} {member.LastName}</td>
                    <td>{member.Nation}</td>
                    {ussaEnabled && <td>{member.MemberNumber || '—'}</td>}
                    <td className="action-buttons">
                      <button onClick={() => handleStartEdit(index)} className="edit-btn">✏️</button>
                      <button onClick={() => handleDelete(index)} className="delete-btn">🗑️</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Add Button */}
      {!showAddForm && (
        <button className="add-jury-btn" onClick={() => setShowAddForm(true)}>
          + Add Jury Member
        </button>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="add-jury-form">
          <h4>Add Jury Member</h4>
          <div className="form-row">
            <label>Function:</label>
            <select
              value={newMember.Function}
              onChange={(e) => setNewMember({ ...newMember, Function: e.target.value })}
            >
              {JURY_FUNCTIONS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label>First Name:</label>
            <input
              type="text"
              value={newMember.FirstName}
              onChange={(e) => setNewMember({ ...newMember, FirstName: e.target.value })}
              placeholder="First name"
            />
          </div>
          <div className="form-row">
            <label>Last Name:</label>
            <input
              type="text"
              value={newMember.LastName}
              onChange={(e) => setNewMember({ ...newMember, LastName: e.target.value })}
              placeholder="Last name"
            />
          </div>
          <div className="form-row">
            <label>Nation:</label>
            <select
              value={newMember.Nation}
              onChange={(e) => setNewMember({ ...newMember, Nation: e.target.value })}
            >
              {NATIONS.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          {ussaEnabled && (
            <div className="form-row">
              <label>USSA ID:</label>
              <div className="input-with-search">
                <button 
                  type="button" 
                  className="search-btn"
                  onClick={() => handleSearch('new')}
                  title="Search USSA members"
                >
                  🔍
                </button>
                <input
                  type="text"
                  value={newMember.MemberNumber}
                  onChange={(e) => setNewMember({ ...newMember, MemberNumber: e.target.value })}
                  placeholder="Member number"
                />
              </div>
            </div>
          )}
          <div className="form-actions">
            <button onClick={handleAddMember} className="confirm-btn">Add Member</button>
            <button onClick={() => setShowAddForm(false)} className="cancel-btn">Cancel</button>
          </div>
        </div>
      )}

      {/* Search Results Modal */}
      {searchResults.length > 0 && (
        <div className="search-modal-overlay" onClick={() => setSearchResults([])}>
          <div className="search-modal" onClick={e => e.stopPropagation()}>
            <div className="search-modal-header">
              <h4>Select Member</h4>
              <button onClick={() => setSearchResults([])}>✕</button>
            </div>
            <table className="search-results-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Name</th>
                  <th>USSA ID</th>
                  <th>State</th>
                  <th>Division</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {[...searchResults]
                  .sort((a, b) => {
                    const statusA = getMemberStatus(a);
                    const statusB = getMemberStatus(b);
                    // Active first, then inactive
                    if (statusA.className === 'status-active' && statusB.className !== 'status-active') return -1;
                    if (statusA.className !== 'status-active' && statusB.className === 'status-active') return 1;
                    return 0;
                  })
                  .map((m, i) => {
                  const status = getMemberStatus(m);
                  return (
                    <tr key={m.id || i} className={status.className}>
                      <td><span className={`member-status ${status.className}`}>{status.label}</span></td>
                      <td>{m.firstName} {m.lastName}</td>
                      <td>{m.id}</td>
                      <td>{m.region || '—'}</td>
                      <td>{m.division || '—'}</td>
                      <td>
                        <button onClick={() => handleSelectResult(m)} className="select-btn">
                          Select
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {isSearching && <div className="searching">Searching...</div>}
    </div>
  );
}

export default JuryForm;
